import memoize from 'memoize-one';
import sma from 'sma';
import {produce} from 'immer';
import {getChartDatapointTotals} from './chartDatapointTotals';
import {getChartDatapointMeans} from './chartDatapointMeans';
import {ChartAggregationCalcType} from 'views/storyboard/utils/types';
import {AreaChartData} from 'views/storyboard/charts/area/AreaChartWrapper';
import {BarChartData} from 'views/storyboard/charts/bar/BarChartWrapper';
import {LineChartData} from 'views/storyboard/charts/line/LineChartWrapper';


export type MovingAverageChartData = (
    AreaChartData | BarChartData | LineChartData
);

const getChartMovingAverage = <T extends MovingAverageChartData>(
    data: T[],
    dataKeys: string[],
    days: number,
    calcType: ChartAggregationCalcType,
    excludedKeys: string[] = [],
) => {
    return memoize(
        // Calculates the total for every key for every data point and adds it together
        (
            data: T[],
            dataKeys: string[],
            days: number,
            calcType: ChartAggregationCalcType,
            excludedKeys = [],
        ): string[] => {
            let aggregatedData;
            if (calcType === ChartAggregationCalcType.Cumulative) {
                aggregatedData = getChartDatapointTotals(data, dataKeys, excludedKeys);
            } else if (calcType === ChartAggregationCalcType.Average) {
                aggregatedData = getChartDatapointMeans(data, dataKeys, excludedKeys);
            } else {
                throw Error('calcType not specified');
            }

            return sma(aggregatedData, days);
        },
    )(data, dataKeys, days, calcType, excludedKeys);
}

export const addMovingAverageToData = <T extends MovingAverageChartData>(
    data: T[],
    dataKeys: string[],
    days: number,
    dataCenter: boolean | undefined,
    calcType: ChartAggregationCalcType,
    excludedKeys: string[] = [],
): T[] => {
    const movingAverage = getChartMovingAverage(data, dataKeys, days, calcType, excludedKeys);
    let lengthDifference: number;
    if (!dataCenter) {
        lengthDifference = days - 1;
    } else {
        lengthDifference = Math.floor((days - 1) / 2);
    }

    return produce(data, (draft) => {
        for (let i = 0; i < movingAverage.length; i++) {
            const index = i + lengthDifference;
            if (draft[index]) {
                // @ts-ignore
                draft[index].movingAvg = movingAverage[i];
            }
        }
    });
};
